import { formatDate, type DateInput } from "@its-engineering-and-development/ui-foundation/utils";

export const COMMENT_CARD_VARIANT = {
    /** Regular comment with a blue accent border. */
    Default: "default",
    /** Regular comment without the blue accent border. */
    Plain: "plain",
    /** Policy violation explanation with a red header and label. */
    Violation: "violation",
} as const;

export type CommentCardVariant = (typeof COMMENT_CARD_VARIANT)[keyof typeof COMMENT_CARD_VARIANT];

interface CommentCardProps {
    /** Comment body text. */
    text: string;
    /** Display name of the comment author. */
    author: string;
    /** Raw date value (ISO string, timestamp or Date); formatted by the component. */
    date: DateInput;
    /** Name of the violated policy; only used by the violation variant. Falls back to a generic label when omitted. */
    policyName?: string | null;
    /** Initials to display next to the policy name; only used by the violation variant. */
    initials?: string | null;
    /** Header text override; only used by the violation variant. Defaults to "Policy Violation Explanation". */
    title?: string;
    /** Visual style of the card. */
    variant?: CommentCardVariant;
}

/** Figma date format, e.g. "6/3/2026, 2:47:25 PM". */
const DATE_FORMAT = "M/d/yyyy, h:mm:ss a";

/**
 * Generic comment card used in communication/comment threads.
 *
 * See {@link COMMENT_CARD_VARIANT} for the available visual styles.
 */
export function CommentCard({ text, author, date, policyName, initials, title, variant = COMMENT_CARD_VARIANT.Default }: CommentCardProps) {
    const formattedDate = formatDate(date, DATE_FORMAT);

    switch (variant) {
        case COMMENT_CARD_VARIANT.Violation:
            return (
                <div className="overflow-hidden rounded-lg border border-red-200">
                    <div className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-3 py-2">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="flex-shrink-0 text-red-500">
                            <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2" />
                            <path d="M6 4v2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                            <circle cx="6" cy="8.5" r="0.6" fill="currentColor" />
                        </svg>
                        <p className="text-xs font-semibold uppercase tracking-wide text-red-600">{title || "Policy Violation Explanation"}</p>
                    </div>
                    <div className="bg-white p-3 md:p-4">
                        <div className="mb-2 flex items-baseline justify-between">
                            <p className="text-sm font-semibold text-gray-900">{author}</p>
                            <p className="text-xs text-gray-400">{formattedDate}</p>
                        </div>
                        {!!policyName && (
                            <p className="mb-1.5 text-xs text-gray-500">
                                <span className="font-medium text-red-600">{policyName}</span>
                                {!!initials && (
                                    <span className="text-gray-400">
                                        {" "}
                                        &middot; Initialed: <span className="font-bold text-gray-900">{initials}</span>
                                    </span>
                                )}
                            </p>
                        )}
                        <p className="text-sm leading-relaxed text-gray-800">{text}</p>
                    </div>
                </div>
            );

        case COMMENT_CARD_VARIANT.Plain:
            return (
                <div className="rounded-lg bg-gray-50 p-4">
                    <p className="mb-2 text-gray-900">{text}</p>
                    <p className="text-sm text-gray-500">
                        {author} - {formattedDate}
                    </p>
                </div>
            );

        default:
            return (
                <div className="rounded border-l-4 border-blue-500 bg-gray-50 p-3 md:p-4">
                    <p className="mb-2 text-sm text-gray-900 md:mb-3 md:text-base">{text}</p>
                    <p className="text-xs text-gray-500 md:text-sm">
                        {author} - {formattedDate}
                    </p>
                </div>
            );
    }
}
